/**
 * GroupSpaceManagement Component
 * 그룹 공간(스터디룸, 회의실) 관리 UI
 */
import React from 'react';
import {
  RectangleGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { useGroupSpaceManagement } from '../use-cases/useGroupSpaceManagement';
import { groupSpaceManagementService } from '../services/GroupSpaceManagementService';

const GroupSpaceManagement: React.FC = () => {
  const {
    activeTab,
    spaces,
    reservations,
    loading,
    error,
    stats,
    handleTabChange,
    handleCreateSpace,
    handleEditSpace,
    handleDeleteSpace,
    handleCreateReservation,
    handleCancelReservation,
  } = useGroupSpaceManagement();

  // 타입별 아이콘 컴포넌트 매핑
  const getTypeIconComponent = (type: 'study_room' | 'meeting_room' | 'private_room') => {
    const iconClass = groupSpaceManagementService.getTypeIcon(type);
    switch (type) {
      case 'study_room':
        return <AcademicCapIcon className={iconClass} />;
      case 'meeting_room':
        return <BuildingOfficeIcon className={iconClass} />;
      case 'private_room':
        return <HomeIcon className={iconClass} />;
      default:
        return <RectangleGroupIcon className={iconClass} />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">그룹 공간 관리</h1>
        <p className="text-gray-600">스터디룸, 회의실 등 그룹 공간을 관리합니다</p>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <RectangleGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">총 공간</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.total_spaces}개</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">활성 공간</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.active_spaces}개</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CalendarIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">총 예약</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.total_reservations}건</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">점유율</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.occupancy_rate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">총 매출</h3>
              <p className="text-2xl font-bold text-gray-900">
                {groupSpaceManagementService.formatPrice(stats.total_revenue)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 탭 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => handleTabChange('spaces')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'spaces'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              공간 관리
            </button>
            <button
              onClick={() => handleTabChange('reservations')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'reservations'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              예약 관리
            </button>
            <button
              onClick={() => handleTabChange('calendar')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'calendar'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              캘린더
            </button>
          </nav>
        </div>
      </div>

      {/* 공간 관리 탭 */}
      {activeTab === 'spaces' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">공간 목록</h2>
            <button
              onClick={handleCreateSpace}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>공간 추가</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {spaces.map((space) => (
              <div
                key={space.id}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${
                  groupSpaceManagementService.getTypeBackgroundClass(space.type)
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {getTypeIconComponent(space.type)}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{space.name}</h3>
                      <p className="text-sm text-gray-500">
                        {groupSpaceManagementService.getTypeName(space.type)}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      groupSpaceManagementService.getActiveBadgeClass(space.is_active)
                    }`}
                  >
                    {groupSpaceManagementService.getActiveStatusName(space.is_active)}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    <span>수용 인원: {space.capacity}명</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                    <span>
                      시간당: {groupSpaceManagementService.formatPrice(space.hourly_rate)}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                    <span>
                      일일: {groupSpaceManagementService.formatPrice(space.daily_rate)}
                    </span>
                  </div>
                </div>

                {space.current_reservation && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-xs text-yellow-800 font-medium mb-1">현재 사용 중</p>
                    <p className="text-sm text-yellow-900">
                      {space.current_reservation.user_name}
                    </p>
                    <p className="text-xs text-yellow-700">
                      {groupSpaceManagementService.formatTime(
                        space.current_reservation.start_time
                      )}{' '}
                      ~{' '}
                      {groupSpaceManagementService.formatTime(
                        space.current_reservation.end_time
                      )}
                    </p>
                  </div>
                )}

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditSpace(space)}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center justify-center space-x-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    <span>수정</span>
                  </button>
                  <button
                    onClick={() => handleDeleteSpace(space.id)}
                    className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 flex items-center justify-center space-x-1"
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span>삭제</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {spaces.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <RectangleGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">등록된 그룹 공간이 없습니다</p>
              <button
                onClick={handleCreateSpace}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                첫 공간 추가하기
              </button>
            </div>
          )}
        </div>
      )}

      {/* 예약 관리 탭 */}
      {activeTab === 'reservations' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">예약 목록</h2>
            <button
              onClick={() => handleCreateReservation()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>예약 추가</span>
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    공간명
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    예약자
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    시작 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    종료 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reservations.map((reservation) => (
                  <tr key={reservation.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {reservation.space_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {reservation.user_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {groupSpaceManagementService.formatDateTime(reservation.start_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {groupSpaceManagementService.formatDateTime(reservation.end_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {groupSpaceManagementService.formatPrice(reservation.payment_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          groupSpaceManagementService.getStatusBadgeClass(reservation.status)
                        }`}
                      >
                        {groupSpaceManagementService.getStatusName(reservation.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {reservation.status !== 'cancelled' && (
                        <button
                          onClick={() => handleCancelReservation(reservation.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          취소
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {reservations.length === 0 && (
              <div className="text-center py-12">
                <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">예약 내역이 없습니다</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 캘린더 탭 */}
      {activeTab === 'calendar' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <CalendarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">캘린더 기능은 준비 중입니다</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupSpaceManagement;
